import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  FiEdit2,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUserCheck,
  FiBriefcase,
  FiFilter,
  FiHash,
  FiUsers,
  FiUpload,
  FiDownload,
  FiFolder,
  FiLock,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Api from '../../Api';
import { usePermission } from '../../hooks/usePermission';
import {
  Button,
  Card,
  Input,
  Modal,
  PageComponent,
  Skeletons,
  Table,
  CustomerImportModal,
  Select,
  MultiSelect,
} from '../../components';
import TableActions from '../../components/Table/TableActions';
import EntityFilesModal from '../../components/EntityFilesModal';
import CustomerForm from '../../components/CustomerForm';
import '../CompanyManager/style.css';

export default function CustomerManager() {
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isFilesModalOpen, setIsFilesModalOpen] = useState(false);
  const [selectedCustomerForFiles, setSelectedCustomerForFiles] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const navigate = useNavigate();
  const permissions = usePermission('management');

  useEffect(() => {
    if (permissions.canView) {
      loadInitialData();
    }
  }, [permissions.canView]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, companyFilter]);

  async function loadInitialData() {
    try {
      setLoading(true);
      const { data: companiesData } = await Api.get('/companies');
      const companyList = companiesData || [];
      setCompanies(companyList);
      setCompanyFilter('all');
      await loadCustomers('all', companyList);
    } catch (err) {
      setError('Erro ao carregar dados iniciais.');
      setLoading(false);
    }
  }

  async function loadCustomers(filterValue = companyFilter, companiesList = companies) {
    try {
      setLoading(true);
      let data = [];

      if (filterValue === 'all') {
        const promises = companiesList.map(comp => 
          Api.getCustomers(comp.id).then(res => 
            Array.isArray(res) ? res.map(c => ({ ...c, company: comp, company_id: comp.id })) : []
          ).catch(() => [])
        );
        const results = await Promise.all(promises);
        data = results.flat();
      } else {
        const res = await Api.getCustomers(filterValue);
        if (Array.isArray(res)) {
          const company = companiesList.find(c => String(c.id) === String(filterValue));
          data = res.map(c => ({ ...c, company, company_id: filterValue }));
        }
      }

      if (Array.isArray(data)) {
        setCustomers(data);
      } else {
        setError('Não foi possível carregar os clientes.');
      }
    } catch (err) {
      setError('Erro ao carregar clientes. Tente novamente.');
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }

  function handleCreate() {
    setSelectedCustomer(null);
    setIsModalOpen(true);
  }

  function handleEdit(customer) {
    setSelectedCustomer(customer);
    setIsModalOpen(true);
  }

  function handleFiles(customer) {
    setSelectedCustomerForFiles(customer);
    setIsFilesModalOpen(true);
  }

  function handleFormSuccess() {
    loadCustomers();
    setIsModalOpen(false);
  }

  function handleExport() {
    const exportData = customers.map(c => ({
      'Empresa': c.company ? (c.company.corporate_name || c.company.trade_name) : 'N/A',
      'Nome / Razão Social': c.name,
      'Nome Fantasia': c.trade_name,
      'Tipo': c.type,
      'CPF / CNPJ': c.document,
      'E-mail': c.email,
      'Telefone': c.phone,
      'Celular': c.mobile,
      'CEP': c.zip_code,
      'Endereço': c.street,
      'Número': c.number,
      'Complemento': c.complement,
      'Bairro': c.neighborhood,
      'Cidade': c.city,
      'Estado': c.state,
      'Observações': c.notes,
      'Ativo': c.active ? 'Sim' : 'Não'
    }));
  
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Clientes");
    XLSX.writeFile(wb, "clientes.xlsx");
  }

  function handleDeleteClick(customer) {
    setCustomerToDelete(customer);
    setIsDeleteModalOpen(true);
  }

  async function handleConfirmDelete() {
    if (!customerToDelete) return;

    // Determine company ID
    const companyId = customerToDelete.company_id || customerToDelete.company?.id || (companyFilter !== 'all' ? companyFilter : null);

    if (!companyId) {
      alert('Erro ao identificar a empresa do cliente.');
      return;
    }

    try {
      setIsDeleting(true);
      await Api.deleteCustomer(companyId, customerToDelete.id);
      
      // Update local state instead of full reload for speed
      setCustomers(prev => prev.filter(c => c.id !== customerToDelete.id));
      
      setIsDeleteModalOpen(false);
      setCustomerToDelete(null);
    } catch (err) {
      console.error(err);
      alert('Erro ao excluir cliente.');
    } finally {
      setIsDeleting(false);
    }
  }

  const companyOptions = useMemo(() => {
    const options = companies.map(c => ({
      value: String(c.id),
      label: c.corporate_name || c.trade_name || `Empresa ${c.id}`,
    }));
    return [{ value: 'all', label: 'TODAS' }, ...options];
  }, [companies]);

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      const allIds = companies.map(c => String(c.id));
      if (specificIds.length === allIds.length && allIds.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);

    const idsWithoutAll = nextValues.filter(v => v !== allValue);
    if (idsWithoutAll.length === 1) {
      const id = idsWithoutAll[0];
      setCompanyFilter(id);
      loadCustomers(id);
    } else {
      setCompanyFilter('all');
      loadCustomers('all');
    }
  }

  const filteredCustomers = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    const allValue = 'all';
    const specificIds = companyFilterValues.filter(v => v !== allValue);
    const hasAllSelected = companyFilterValues.includes(allValue);

    return customers.filter(customer => {
      const matchesSearch =
        customer.name?.toLowerCase().includes(searchLower) ||
        customer.trade_name?.toLowerCase().includes(searchLower) ||
        customer.document?.toLowerCase()?.includes(searchLower) ||
        customer.email?.toLowerCase().includes(searchLower) ||
        customer.company?.trade_name?.toLowerCase().includes(searchLower);

      const companyId = customer.company?.id || customer.company_id;
      const matchesCompany =
        hasAllSelected ||
        specificIds.length === 0 ||
        specificIds.includes(String(companyId));

      return matchesSearch && matchesCompany;
    });
  }, [customers, searchTerm, companyFilterValues]);

  const totalItems = filteredCustomers.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedCustomers = filteredCustomers.slice(
    startIndex,
    startIndex + pageSize,
  );

  const columns = [
    {
      id: 'customer',
      header: 'Cliente',
      width: '2.4fr',
      cellClassName: 'manager-table-cell-primary',
      render: customer => (
        <>
          <div className="manager-table-avatar">
            {customer.type === 'PJ' ? (
              <FiBriefcase size={18} />
            ) : (
              <FiUsers size={18} />
            )}
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">
              {customer.name}
            </span>
            <span className="manager-table-subtitle">
              {customer.trade_name || `ID: ${customer.id}`}
            </span>
          </div>
        </>
      ),
    },
    {
      id: 'document',
      header: 'Documento',
      width: '1.8fr',
      cellClassName: 'manager-table-cell manager-table-col-cnpj',
      render: customer => (
        <div className="manager-table-cnpj">
          <FiHash size={14} />
          <span>{customer.document || 'Não informado'}</span>
        </div>
      ),
    },
    {
      id: 'contact',
      header: 'Contato',
      width: '2fr',
      cellClassName: 'manager-table-cell',
      render: customer => {
        const email = customer.email || 'E-mail não informado';
        const phone = customer.mobile || customer.phone || 'Telefone não informado';

        return (
          <div className="manager-table-cell-text">
            <span className="manager-table-subtitle">
              {email}
            </span>
            <span className="manager-table-subtitle">
              {phone}
            </span>
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      width: '1.4fr',
      cellClassName: 'manager-table-cell manager-table-col-tags',
      render: customer => {
        const isActive = customer.active !== false;

        return (
          <div className="manager-tags">
            <span
              className={[
                'manager-tag',
                isActive ? 'manager-tag-success' : 'manager-tag-danger',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {isActive ? 'Ativo' : 'Inativo'}
            </span>
          </div>
        );
      },
    },
    {
      id: 'company',
      header: 'Empresa',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: customer => {
        const companyName = customer.company ? (customer.company.corporate_name || customer.company.trade_name) : 'N/A';
        return <span className="manager-table-text" style={{ fontWeight: 'normal' }}>{companyName}</span>;
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: customer => (
        <TableActions>
          {permissions.canEdit && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(customer);
                }}
              >
                <FiEdit2 size={16} />
                Editar
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleFiles(customer);
                }}
              >
                <FiFolder size={16} />
                Arquivos
              </button>
            </>
          )}
          {permissions.canDelete && (
            <button
              type="button"
              className="action-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(customer);
              }}
            >
              <FiTrash2 size={16} />
              Excluir
            </button>
          )}
        </TableActions>
      ),
    },
  ];

  function CustomerManagerSkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  const isMobile = window.innerWidth <= 768;

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiLock size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar esta página.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && !hasLoadedOnce && (
          <CustomerManagerSkeletonWrapper />
        )}

        {(hasLoadedOnce || !loading) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiUserCheck size={22} />
                </div>
                <div>
                  <h1>Gestão de Clientes</h1>
                  <p>
                    Visualize, filtre e gerencie todos os clientes da empresa selecionada.
                  </p>
                </div>
              </div>
              <div className="manager-header-actions" style={{ display: 'flex', gap: '8px' }}>
                {permissions.canCreate && (
                  <>
                    <Button onClick={() => setIsImportModalOpen(true)} className="manager-header-button" variant="secondary">
                        <FiUpload size={18} />
                        {!isMobile && 'Importar'}
                    </Button>
                    <Button onClick={handleExport} className="manager-header-button" variant="secondary">
                        <FiDownload size={18} />
                        {!isMobile && 'Exportar'}
                    </Button>
                    <Button onClick={handleCreate} className="manager-header-button">
                        <FiPlus size={18} />
                        Cliente
                    </Button>
                  </>
                )}
              </div>
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {customers.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredCustomers.length} de {customers.length} clientes
                  </span>
                )}
              </div>

              <div className="manager-filters-grid-custom">
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Empresa</label>
                  <MultiSelect
                    id="company-filter"
                    options={companyOptions}
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    searchable
                  />
                </div>
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Buscar</label>
                  <div className="manager-filter-search">
                    <Input
                      id="customer-search"
                      placeholder="Busque por nome, documento ou e-mail"
                      value={searchTerm}
                      onChange={event =>
                        setSearchTerm(event.target.value)
                      }
                      icon={FiSearch}
                    />
                  </div>
                </div>
              </div>
            </Card>

            {error && <div className="error">{error}</div>}

            {!error && customers.length === 0 && (
              <div className="empty-state">
                <p>Nenhum cliente cadastrado.</p>
              </div>
            )}

            {!error && customers.length > 0 && (
              <Table
                items={paginatedCustomers}
                columns={columns}
                totalItems={totalItems}
                page={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={value => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                getRowKey={customer => customer.id}
                emptyMessage="Nenhum cliente encontrado."
              />
            )}
          </>
        )}
      </div>

      <EntityFilesModal
        isOpen={isFilesModalOpen}
        onClose={() => setIsFilesModalOpen(false)}
        entity={selectedCustomerForFiles}
        entityType="customer"
        company={selectedCustomerForFiles?.company || companies.find(c => String(c.id) === String(companyFilter))}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedCustomer ? 'Editar Cliente' : 'Novo Cliente'}
        size='xl'
      >
        <CustomerForm
          companyId={companyFilter !== 'all' ? companyFilter : null}
          initialData={selectedCustomer}
          onSuccess={handleFormSuccess}
          onCancel={() => setIsModalOpen(false)}
          companies={companies}
        />
      </Modal>

      {companyFilter !== 'all' || true ? (
        <CustomerImportModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onSuccess={() => loadCustomers(companyFilter)}
          companyId={companyFilter !== 'all' ? companyFilter : null}
          companies={companies}
        />
      ) : null}

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmar Exclusão"
      >
        <div className="delete-modal-content">
          <p>
            Tem certeza que deseja excluir o cliente{' '}
            <span className="delete-modal-company-name">
              {customerToDelete?.name}
            </span>
            ?
          </p>
          <p>Essa ação não poderá ser desfeita.</p>
        </div>
        <div className="delete-modal-actions">
          <Button
            type="button"
            className="cancel-button"
            onClick={() => setIsDeleteModalOpen(false)}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </div>
      </Modal>
    </PageComponent>
  );
}
